import React, { useState, useRef, useEffect } from "react";
import SendIcon from "@mui/icons-material/Send";
import "./messages.css";
import "./LfyApp.css";
import axios from "axios";
import { db } from '../lib/firebaseConfig';
import { collection, serverTimestamp, addDoc, orderBy, query, onSnapshot, getDocs, doc } from "firebase/firestore";
import { useAuth } from "../hooks/auth";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { Chip } from "@mui/material";
import sparklesSound from '../sound/sparkles-sound.mp3';
import ThinkingAnimation from './ThinkingAnimation';

// Set your OpenAI API key here
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;





function LfyApp() {
  const [userInput, setUserInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const chatboxRef = useRef(null);
  const { user } = useAuth();
  const [aiResponseTextToCopy, setAiResponseTextToCopy] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [aiResponseCopied, setAiResponseCopied] = useState(false);
  const [aiResponseHoveredIndex, setAiResponseHoveredIndex] = useState(-1);
  const [showStars, setShowStars] = useState(false);
  const [userName, setUserName] = useState(""); // Add this state
  const [userEmail, setUserEmail] = useState(""); // Add this state
  const [statistics, setStatistics] = useState({
    nbConversation: 0,
    nbMessages: 0,
    userLocation: [],
    userMessages: [],
    userAllMessages: [],
    userAllTimeStamps: [],
    averageSessionLength: '0:00:00',
    activeUsers: 0,
  });

  const handleChatWithMeClick = async () => {
    if (!user) {
      alert("Please log in or sign up to start the conversation.");
      return;
    }

    setShowStars(true);
    const audio = new Audio(sparklesSound);
    audio.play();

    const firstMessage = "Hi Luna, I'd like to know my stats and how I can improve them 😄";
    try {
      setUserInput("");
      setIsSending(true);

      const userMessageData = {
        type: "user",
        text: firstMessage,
        timestamp: serverTimestamp(),
      };

      await addMessageToUserChat(user.uid, userMessageData, "messages");

      const aiResponse = await sendMessageToAI(firstMessage);

      const aiResponseData = {
        type: "ai",
        text: aiResponse,
        timestamp: serverTimestamp(),
      };

      await addMessageToUserChat(user.uid, aiResponseData, "messages");

      setTimeout(() => {
        setShowStars(false);
      }, 3000);

      setIsSending(false);
    } catch (error) {
      console.log(error);
      setIsSending(false);
    }
  };

  const sendMessageToAI = async (userMessage) => {
    const apiUrl = "https://api.openai.com/v1/completions";
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${OPENAI_API_KEY}`,
    };

    let userInputWithPunctuation = userMessage;
    if (!/[.?!]\s*$/.test(userMessage)) {
      userInputWithPunctuation += ". \n";
    }

    const LUNA_PROMPT = updateLunaPrompt(userInputWithPunctuation);

    const data = {
      prompt: LUNA_PROMPT,
      temperature: 0.9,
      max_tokens: 250,
      top_p: 1,
      frequency_penalty: 0.6,
      presence_penalty: 0.6,
      stop: [" \stop", " AI:"],
      model: "text-davinci-003",
    };

    try {
      const response = await axios.post(apiUrl, data, { headers });
      return response.data.choices[0].text.trim();
    } catch (error) {
      console.log("Error sending message to OpenAI API:", error);
      return "Sorry, I couldn't process your request at the moment.";
    }
  };

  const updateLunaPrompt = (userInputWithPunctuation) => {
    const {
      nbConversation,
      nbMessages,
      userLocation,
      userMessages,
      userAllMessages,
      userAllTimeStamps,
      averageSessionLength,
      activeUsers,
    } = statistics;

    // Update the LUNA_PROMPT with actual statistics data
    const LUNA_PROMPT = `Hey ${userName}. I am Luna, a virtual assistant powered by AI and part of Luna Speak, a leading software company. Our platform offers businesses the ability to integrate a powerful chatbot named Luna directly into their website, enabling seamless interactions with their customers. I will greet you and talk to you by calling you ${userName}.

    As a virtual assistant, I am designed to enhance you customer satisfaction regarding to your stats. My AI capabilities enable me to understand natural language and provide relevant and helpful responses to your customers.

    Please note that our chatbot is already built by us (Luna Speak) and not editable by you. It's designed to provide efficient and effective customer support on your website. If you want to improve the quality of the chatbot you can do it by optimizing the text you have provided when you have configured the chatbot. The best are the text, details about your products, your business, the more the chatbot will be efficient.
    
    Here is the link to edit your chatbot : app.luna-speak.com/ai-settings
    Here is the link to add/remove/edit some manually added products : app.luna-speak.com/products-list

    For your shopify products that are connected to our app you should edit/remove/add them directly from your Shopify store.

    I am an AI but i will NOT speak about AI in our conversation, or any technical terms.
    I will be short, precise, efficient, and kind.

    I will only tell you how you can improve your website, related to your chatbot stats. But except of how to improve the quality of the response from Luna Chatbot, i will tell you to personalize the Luna Chatbot etc.

    I will not tell you about adding personalized messages in the chatbot because we do it already.

    Let me provide to you ${userName} some feedback based on your stats:
    - Number of Conversations: ${nbConversation}
    - Number of Messages: ${nbMessages}
    - User Location: ${userLocation}
    - Your Recent Messages: ${userMessages}
    - All Your Messages: ${userAllMessages}
    - All Timestamps of Your Messages: ${userAllTimeStamps}
    - Average Session Length: ${averageSessionLength}
    - Active Users: ${activeUsers}

    Here is your email if needed, i can contact you there : ${userEmail}. If you need you can change it from your Luna settings page.

    Based on your stats, I will provide you some strategic tips for improving your customer service experience and enhancing your statistics with Luna. If you have any questions or need further assistance, feel free to ask. How can I help you implement these strategies and further improve your customer interactions with Luna? Also, feel free to add relevant emojis to your responses to make the interactions more engaging and friendly!`;

        return `${LUNA_PROMPT}${userInputWithPunctuation} \n`;

    };

    useEffect(() => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const unsubscribe = onSnapshot(userRef, (doc) => {
          if (doc.exists()) {
            const userData = doc.data();
            setUserName(userData.name);
            setUserEmail(userData.email); // Set the user's email
            console.log(userData.email, userData.name)
          }
        });
        return unsubscribe;
      }
    }, [user]);
    

    const fetchStatisticsData = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "statistics"));
        const statisticsData = querySnapshot.docs.map(doc => doc.data());
        console.log("Statistics data:", statisticsData);
        return statisticsData;
      } catch (error) {
        console.error("Error fetching statistics data:", error);
        return [];
      }
    };
  
    async function addMessageToUserChat(userUid, message) {
      const userChatRef = collection(db, "chatMessages", userUid, "messages");
      await addDoc(userChatRef, message);
    }

    const handleAiResponseCopySuccessClose = () => {
      setAiResponseCopied(true);
      setTimeout(() => {
        setAiResponseCopied(false);
      }, 2000); // Adjust the time as needed
    };
    
  
    const fetchChatMessages = (user) => {
      if (!user) {
        return;
      }
  
      const userChatRef = collection(db, "chatMessages", user.uid, "messages");
      const q = query(userChatRef, orderBy("timestamp"));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messages = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setChatMessages(messages);
      });
  
      return unsubscribe;
    };
  
    useEffect(() => {
      if (user) {
        const unsubscribe = fetchChatMessages(user);
        return () => {
          unsubscribe();
        };
      }
    }, [user]);
  
    const handleSubmit = async (event) => {
      event.preventDefault();
  
      if (!userInput) return;
  
      try {
        setUserInput("");
        setIsSending(true);
  
        const userMessageData = {
          type: "user",
          text: userInput,
          timestamp: serverTimestamp(),
        };
  
        await addMessageToUserChat(user.uid, userMessageData, "messages");
  
        const aiResponse = await sendMessageToAI(userInput);
  
        const aiResponseData = {
          type: "ai",
          text: aiResponse,
          timestamp: serverTimestamp(),
        };
  
        await addMessageToUserChat(user.uid, aiResponseData, "messages");
  
        setIsSending(false);
      } catch (error) {
        console.log(error);
        setIsSending(false);
      }
    };
  
    useEffect(() => {
      const fetchData = async () => {
        const data = await fetchStatisticsData();
        if (data.length > 0) {
          setStatistics(data[0]);
        }
      };
      fetchData();
    }, []);
  
    useEffect(() => {
      updateLunaPrompt("");
    }, [statistics]);
  
    useEffect(() => {
      if (chatboxRef && chatboxRef.current) {
        const element = chatboxRef.current;
        element.scroll({
          top: element.scrollHeight,
          left: 0,
          behavior: "smooth",
        });
      }
    }, [chatboxRef, chatMessages]);

  return (
    <div className="web-message-page">
      <div className="web-message-container">
        {/* CHATBOX */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            paddingTop: "20px",
            justifyContent: "flex-start",
            flex: "center",
            alignContent: "center",
            width: '100%'
          }}
        >
          <div className="message-wrapper">
        <div className="web-message-container__chatbox" ref={chatboxRef}>
          {chatMessages.length === 0 ? (
            // Display "Chat with me" when chatMessages is empty
            <div className="chat-placeholder" onClick={handleChatWithMeClick}>Chat with me ✨</div>
            ) : (
            // If chatMessages is not empty, render chat messages
            chatMessages.map((message, index) => (
              <div
              key={index}
              className={`${message.type === "ai" ? "bot-response-inbox" : "user-response-inbox"}`}
              onMouseEnter={() => setAiResponseHoveredIndex(index)}
              onMouseLeave={() => setAiResponseHoveredIndex(-1)}
              >
                {message.type === "ai" && (
                  <div style={{ position: "relative" }}>
                    <span
                      className="bot-name"
                      style={{
                        position: "absolute",
                        transform: "translate(50%, -30px)",
                        top: "0",
                        right: "0",
                        fontSize: "10px",
                      }}
                    >
                      Luna
                    </span>
                    {/* AI response text with copy icon */}
                    <div style={{ position: "relative" }}>
                      {message.text}
                      <FileCopyIcon
                        className="copy-icon"
                        style={{
                          position: "absolute",
                          top: "0",
                          right: "-5px",
                          cursor: "pointer",
                          fontSize: "14px",
                          visibility: aiResponseHoveredIndex === index
                          ? "visible"
                          : "hidden",
                        }}
                        onClick={() => {
                          setAiResponseTextToCopy(message.text);
                          navigator.clipboard.writeText(message.text);
                          // Hide the copy icon after copying
                          setAiResponseHoveredIndex(-1);
                          // Show the copy success chip
                          handleAiResponseCopySuccessClose();
                        }}
                      />
                      {aiResponseHoveredIndex === index && aiResponseCopied && (
                        <Chip
                        label="Copied!"
                        color="primary"
                        style={{
                          position: "absolute",
                          top: "-20px",
                          right: "5px",
                        }}
                        />
                        )}
                    </div>
                  </div>
                )}

                {message.type === "user" && (
                  <div
                  style={{ position: "relative" }}
                  onMouseEnter={() => setCopySuccess(false)}
                  >
                    <span
                      className="user-name"
                      style={{
                        position: "absolute",
                        transform: "translate(-50%, -30px)",
                        top: "0",
                        left: "0%",
                        fontSize: "10px",
                      }}
                    >
                      You
                    </span>
                    {/* User message */}
                    <div>{message.text}</div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Display the shiny stars when showStars is true */}
      {showStars && (
        <div className="shiny-stars">
              {[...Array(20)].map((_, index) => (
                <div
                key={index}
                className="star"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 1}s`,
                }}
                />
                ))}
            </div>
          )}
      
          <div className="web-message-form" style={{width: '70%', margin: 'auto auto'}}>
          
          {isSending && <ThinkingAnimation />}

            <form onSubmit={handleSubmit}>
              <div className="form-inputs">
                <textarea
                  name=""
                  id="web-message-input"
                  cols="30"
                  rows="10"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSubmit(e);
                    }
                  }}
                  placeholder="Type your message..."
                />

                <div
                  className="form-options"
                  style={{
                    display: "flex",
                    flexDirection: "column-reverse",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "25px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      gap: "5px",
                      width: "100%",
                    }}
                  >

                    <button
                      className="submit-btn"
                      type="submit"
                      disabled={isSending}
                    >
                      {isSending ? "Sending..." : "Send message"} <SendIcon />
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LfyApp;
